<template>
  <div v-if="loading">
    <spinner />
  </div>
  <div v-else>
    <h5 class="my-0 mb-2 header-info text-center">
      {{ dayjs(fullGame.start).format('DD MMM hh:mm A') }}
      - {{ fullGame.participants[0].longName }} at
      {{ fullGame.participants[1].longName }}
      <span v-if="periodLabel" class="period-pill">{{ periodLabel }}</span>
      <h5
        v-if="fullGame.participants[0].mainPitcher"
        style="display: inline"
      >
        ({{ fullGame.participants[0].mainPitcher }})
      </h5>
    </h5>
    <h1 class="my-md-0 mt-3 mb-0 media text-center">
      <span class="header-info" :class="isBestOffer">
        <div
          v-if="fullGame.league && fullGame.league.includes('PROPS')"
          class="propStyle"
          :class="{ 'mb-2': isNewOffer, 'mr-2': !isNewOffer }"
        >
          {{ formatEventNameForDisplay(fullGame.eventName) }}
        </div>
        <span v-if="!isNewOffer">{{ formattedSideString }}</span>
        <div
          :class="{
            'd-inline': !isNewOffer,
            'row m-0 inputs-div flex-nowrap': isNewOffer,
          }"
        >
          <div
            v-if="isNewOffer"
            class="side-modal-select-container d-inline-flex"
            :class="{
              'side-modal-select-container-moneyline':
                session.type.includes('moneyline'),
            }"
          >
            <select
              id="createSide"
              v-model="selectedSide"
              name="sortBy"
              class="side-modal-select"
              :class="{
                'side-modal-select-moneyline':
                  session.type.includes('moneyline'),
              }"
              @change="sendSide()"
            >
              <option
                v-for="(value, index) in modalSideOptions"
                :key="index"
                :value="value"
              >
                {{
                  session.type !== 'spread' || screenWidth > 767
                    ? value.longName
                    : value.shortName
                }}
              </option>
            </select>
            <font-awesome-icon
              :icon="faChevronDown"
              class="side-modal-select-chevron"
            />
          </div>
          <input
            v-if="isNewOffer && !session.type.includes('moneyline')"
            :value="spreadTotal"
            class="d-inline-flex spread-modal-input"
            :class="{ 'spread-modal-input--invalid': lineNumberError }"
            :aria-invalid="!!lineNumberError"
            @input="spreadTotal = $event.target.value"
            @keyup="sendSpread(spreadTotal)"
          />
          <odds-selector
            :odds="offerOdds"
            class="d-inline-flex flex-grow-1 mobileIsNewOffer"
            @update-odds="updateOdds"
          />
        </div>
      </span>
    </h1>
    <p v-if="lineNumberError" class="line-error" role="alert">
      {{ lineNumberError }}
    </p>
    <alert-notification
      v-if="!fullGame.live && Number(expiry) > endExpiry"
      type="warning"
      class="mt-2 mb-3"
    >
      <font-awesome-icon :icon="faExclamationTriangle" class="mr-2" />
      <span class="font-regular"
        >This order will expire when event starts!</span
      >
    </alert-notification>
    <div
      v-if="canOfferAsArb"
      class="arb-card"
      :class="{ 'is-on': arbOfferEnabled }"
      role="switch"
      :aria-checked="arbOfferEnabled"
      tabindex="0"
      @click="toggleArbOffer"
      @keydown.space.prevent="toggleArbOffer"
      @keydown.enter.prevent="toggleArbOffer"
    >
      <div class="arb-card-message">
        <font-awesome-icon
          :icon="arbOfferEnabled ? faBolt : faExclamationTriangle"
          class="arb-card-status-icon"
        />
        <span v-if="arbOfferEnabled" class="arb-card-text">
          Arb Offer on &mdash; you stay a maker and avoid taker fees.
        </span>
        <span v-else class="arb-card-text">
          {{ arbOrderMessage }}
        </span>
      </div>
      <div class="arb-card-controls">
        <div class="arb-card-title">
          <span class="arb-card-icon">
            <font-awesome-icon :icon="faBolt" />
          </span>
          <span class="arb-card-label">Arb Offer</span>
        </div>
        <a
          class="arb-card-learn"
          href="#"
          @click.stop.prevent="openArbOfferInfo"
        >Learn more</a>
        <span class="arb-card-spacer"></span>
        <span class="arb-card-state">{{ arbOfferEnabled ? 'On' : 'Off' }}</span>
        <span class="toggle-switch" aria-hidden="true">
          <span
            class="toggle-slider"
            :class="{ 'is-on': arbOfferEnabled }"
          ></span>
        </span>
      </div>
    </div>
    <div class="flex-nowrap align-items-center mt-3">
      <bid-ask-ladder
        :key="'bidaskladder'"
        :full-game="fullGame"
        :side="side"
        :offer-mode="true"
        :your-side-name="getSideString(fullGame, session, false)"
        :other-side-name="getOtherSideString(fullGame, session, false)"
        :take-bet-mode="true"
        :left-elements="
          betType === 'spread' ? alternativeSpreads?.away : alternativeTotals
        "
        :right-elements="
          betType === 'spread' ? alternativeSpreads?.home : alternativeTotals
        "
        :risk="effectiveRiskForLadder"
        :selected-bid-ids="selectedBidIds"
        :bid-ladder="bidLadder"
        :ask-ladder="askLadderComputed"
        :spread-number="spread"
        :total-number="total"
        :offer-is-in-arb="isInArb && !arbOfferEnabled"
        @select-bids="handleSelectBids"
        @open-dropdown="openDropdown"
        @add-width-to-bid-ladder="passWidthLadderArray"
      />
    </div>
    <div class="row risk-win-items mt-3">
      <div class="col-sm-4 col-12 align-items-center pr-2">
        <div
          class="input-group d-flex align-items-center"
          :class="{ 'error-outline': offerBelowMinimum }"
        >
          <input
            v-two-decimal
            maxlength="8"
            :placeholder="''"
            :value="offerRisk"
            class="d-inline-flex input-width"
            @input="handleOfferRiskUpdate($event.target.value)"
            @focus="onFocusValues.createOfferRisk = true"
            @blur="onFocusValues.createOfferRisk = false"
          />
          <div
            class="input-group-div"
            @click="dropdownOfferRisk = !dropdownOfferRisk"
          >
            Risk
            <font-awesome-icon
              :icon="dropdownOfferRisk ? faAngleUp : faAngleDown"
              role="button"
              class="ml-2"
            />
            <div v-if="dropdownOfferRisk" class="dropdown-menu">
              <div
                v-for="item in riskWinOptions"
                :key="item.concat('RISK')"
                @click="
                  offerFocustInput = 'offerRisk';
                  handleOfferRiskUpdate(parseFloat(item));
                "
              >
                {{ item }}
              </div>
            </div>
          </div>
        </div>
        <div class="order-error-message">Minimum offer is to win $25</div>
      </div>
      <div class="col-sm-4 col-12 align-items-center pr-2 pl-2">
        <div
          class="input-group d-flex align-items-center"
          :class="{ 'error-outline': offerBelowMinimum }"
        >
          <input
            v-two-decimal
            maxlength="8"
            :placeholder="''"
            :value="offerWin"
            class="d-inline-flex input-width"
            @input="handleOfferWinUpdate($event.target.value)"
            @focus="onFocusValues.createOfferWin = true"
            @blur="onFocusValues.createOfferWin = false"
          />
          <div
            class="input-group-div"
            @click="dropdownOfferWin = !dropdownOfferWin"
          >
            To Win
            <font-awesome-icon
              :icon="dropdownOfferWin ? faAngleUp : faAngleDown"
              role="button"
              class="ml-2"
            />
            <div v-if="dropdownOfferWin" class="dropdown-menu">
              <div
                v-for="item in riskWinOptions"
                :key="item"
                @click="
                  offerFocustInput = 'offerWin';
                  handleOfferWinUpdate(parseFloat(item));
                "
              >
                {{ item }}
              </div>
            </div>
          </div>
        </div>
        <div class="order-error-message">Minimum offer is to win $25</div>
      </div>
      <div class="col-sm-4 col-12 align-items-center pl-2">
        <div class="input-group d-flex align-items-center">
          <input
            v-two-decimal
            :value="expiry"
            class="d-inline-flex input-width left"
            @input="handleExpiryUpdate($event.target.value)"
            @blur="onFocusValues.expiry = false"
          />
          <!-- placeholder="minutes" -->
          <div
            class="input-group-div"
            @click="dropdownExpiry = !dropdownExpiry"
          >
            Expiry
            <font-awesome-icon
              :icon="dropdownExpiry ? faAngleUp : faAngleDown"
              role="button"
              class="ml-2"
            />
            <div v-if="dropdownExpiry" class="dropdown-menu expiry">
              <div
                v-for="(item, i) in expiryItems"
                :key="i"
                @click="
                  offerFocustInput = 'expiry';
                  expiry = parseFloat(item.value);
                "
              >
                {{ item.text }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="canSeeRebates(userStore.user.username) && offerRisk !== '' && parseFloat(offerRisk) >= 1 && parseFloat(offerWin) > 0" class="rebate-line mt-3">
      <span class="rebate-line__label">Offer Rebate (0.1%)</span>
      <span class="rebate-line__value">+${{ calculateRebateAmount(offerRisk, offerWin).toFixed(2) }}</span>
    </div>
    <alert-notification v-if="isOverAvailable" type="error" class="mt-3">
      <font-awesome-icon :icon="faExclamationTriangle" class="mr-2" />
      <span class="font-regular"
        >Risk > Available Balance (<a
          class="add-balance"
          @click="handleOfferRiskUpdate(noDecimal(available))"
          >{{ Math.floor(available) }}</a
        >)</span
      >
    </alert-notification>
    <alert-notification
      v-if="isOverMaxLiability"
      type="primary"
      :class="{
        'mt-3': isOverAvailable,
        'mt-4': !isOverAvailable,
      }"
    >
      <font-awesome-icon
        :icon="faCircleExclamation"
        class="mr-1"
        color="#343b86"
      />
      <span class="mr-1">Max Risk = ${{ userStore.user.maxLiability }}.</span>
      <span v-if="offerRiskValue <= 100000" class="font-regular">
        <a
          class="add-available-risk"
          @click="updateMaxRisk(offerRiskValue)"
          >Update to ${{ offerRisk }}</a
        >
      </span>
      <span v-else class="font-regular">
        Maximum allowed is $100,000
      </span>
    </alert-notification>
    <div v-if="userStore.user.isLoggedIn" class="row mt-4">
      <div
        class="col-md-6 mb-3"
        style="text-align: -webkit-center"
        :class="{
          'mx-auto': isPro,
          'col-md-12': isNewOffer,
        }"
      >
        <slide-to-confirm
          v-if="isMobileViewport"
          :disabled="offerConfirmDisabled"
          :loading="loading"
          :variant="exceedsBalanceButCanOfferMax ? 'max' : 'offer'"
          armed-text="Release to place offer"
          aria-label="Slide to place offer"
          @confirm="onSlideConfirm"
          @abandon="onSlideAbandon"
        >
          <b>
            Slide to offer
            <span v-if="userStore.user.oddsFormat !== 'decimal'">
              {{ formatOddsNoCommission(offerOdds) }} | ${{
                exceedsBalanceButCanOfferMax
                  ? maxRiskForDisplay.toFixed(2)
                  : offerRisk === ''
                    ? 0
                    : roundToFixed2(offerRisk)
              }}
            </span>
            <span v-else>
              {{ oddsComputed }} | ${{
                exceedsBalanceButCanOfferMax
                  ? maxRiskForDisplay.toFixed(2)
                  : offerRisk === ''
                    ? 0
                    : roundToFixed2(offerRisk)
              }}
            </span>
          </b>
          <span>
            to win ${{
              exceedsBalanceButCanOfferMax
                ? maxWinForDisplay.toFixed(2)
                : offerWin === ''
                  ? 0
                  : roundToFixed2(offerWin)
            }}
          </span>
        </slide-to-confirm>
        <button
          v-else
          :disabled="offerConfirmDisabled"
          :class="['create-offer-btn', { 'col-md-6': isNewOffer, 'btn-max-balance': exceedsBalanceButCanOfferMax }]"
          @click="exceedsBalanceButCanOfferMax ? createOfferAtMax() : createOffer()"
        >
          <b>
            Offer
            <span v-if="userStore.user.oddsFormat !== 'decimal'">
              {{ formatOddsNoCommission(offerOdds) }} | ${{
                exceedsBalanceButCanOfferMax ? maxRiskForDisplay.toFixed(2) : (offerRisk === '' ? 0 : roundToFixed2(offerRisk))
              }}
            </span>
            <span v-else>
              {{ oddsComputed }} | ${{
                exceedsBalanceButCanOfferMax ? maxRiskForDisplay.toFixed(2) : (offerRisk === '' ? 0 : roundToFixed2(offerRisk))
              }}
            </span>
          </b>
          <br />
          to win ${{ exceedsBalanceButCanOfferMax ? maxWinForDisplay.toFixed(2) : (offerWin === '' ? 0 : roundToFixed2(offerWin)) }}
        </button>
      </div>
      <div v-if="!isPro && !isNewOffer" class="col-md-6">
        <button class="take-bet-btn" @click="handleReturnToParentModal">
          <b> Return to Take Bet </b>
          <br class="font-weight-light" />
          {{ getSideString(fullGame, session) }} {{ formatOdds(session.odds) }}
        </button>
      </div>
    </div>
    <div v-else class="row mt-4 justify-content-center">
      <div class="col-xs-12 col-md-6">
        <button
          class="take-bet-btn"
          @click="checkAuthAndShowLogin"
        >
          <b> You must be logged in to bet </b>
          <br />
          Login or signup by clicking here
        </button>
      </div>
    </div>
    <div v-if="userStore.user.isLoggedIn" class="row my-0 mt-0 mb-0 py-0">
      <div class="col-md-6 col-sm-12" :class="{ 'col-md-12': isNewOffer }">
        <h6 class="text-black-75 text-center my-md-0 my-3">
          <font-awesome-icon :icon="faEye" class="mr-1" />
          <win-to-risk-phrase
            :session="sessionOthersSee"
            :odds="-sessionOthersSee.odds"
            :league="fullGame.league?.toLowerCase()"
            :side-type="session.type"
          />
        </h6>
      </div>
      <div
        v-if="!isNewOffer"
        class="col-md-6 col-sm-12 d-flex justify-content-center align-items-center"
      >
        <h6 class="text-black-75 my-0 mr-2">
          <font-awesome-icon :icon="faTelegram" class="mr-1" />
          Send the offer to the

          <a
            href="https://t.me/joinchat/VDoTLW06tiqChDUj"
            target="_blank"
            rel="noopener"
          >
            orders chat?
          </a>
        </h6>
        <tick
          :pressed="sendMessage"
          class="telegramTick"
          @update:pressed="handlePressedUpdate"
        />
      </div>
    </div>
    <SelectSpreadDropdown
      v-if="bidLadderSpread && ['total', 'spread'].includes(session.type)"
      v-show="session.type === 'spread' ? awaySpreadPopUp : totalPopUp"
      :game="fullGame"
      :bid-ladder-spread="bidLadderSpread"
      :left-elements="
        session.type === 'spread' ? alternativeSpreads?.away : alternativeTotals
      "
      :right-elements="
        session.type === 'spread' ? alternativeSpreads?.home : alternativeTotals
      "
      :popup="session.type === 'spread' ? 'awaySpreadPopUp' : 'totalPopUp'"
      @select-spread="handleSelectSpreadOrTotal"
    />
  </div>
</template>

<script setup>
import InformationalTooltip from '@/components/global/tooltips/InformationalTooltip.vue';
import BidAskLadder from '../BidAskLadder.vue';
import AlertNotification from '@/components/betting/BetSlipAlert.vue';
import SelectSpreadDropdown from '@/components/betting/SelectSpreadDropdown.vue';
import OddsSelector from '@/components/betting/OddsSelector.vue';
import Tick from '@/components/global/Tick.vue';
import WinToRiskPhrase from '@/components/betting/WinToRiskPhrase.vue';
import Spinner from '@/components/global/Spinner.vue';
import SlideToConfirm from '@/components/global/SlideToConfirm.vue';
import dayjs from 'dayjs';
import { ref, computed, onMounted, onBeforeUnmount, toRaw } from 'vue';
import { calculateWinForNoVig } from '@/core/commissionUtils';
import { calculateRebateAmount, canSeeRebates } from '@/core/rebateUtils';
import {
  getSideString,
  getOtherSideString,
  roundToFixed2,
  noDecimal,
  getOtherSideType,
  calculateWin,
  calculateRisk,
  getAnchoredOfferSide,
  convertAmericanToDecimal,
  convertAmericanToImpliedProbability,
  convertDecimalToAmerican,
  currentDevice,
  convertImpliedProbabilityToAmerican,
  formatDecimalOddsForInput,
  roundTo3,
  formatEventNameForDisplay,
  getDrawLabel,
  getMarketSubtype,
} from '@/core/utils';
import { getAmericanTickInfo } from '@/core/ticks';
import { checkAuthAndShowLogin } from '@/core/authUtils';
import { getPeriodPrefix } from '@/consts/teamsDisplayNames';
import PostHogUtils from '@/core/PostHogUtils';
import { getSideStringForModals } from '@/core/modalUtils';
import { formatOdds, formatOddsNoCommission } from '@/core/oddsFormatUtils';
import {
  riskWinOptions,
  expiryItems,
  alwaysAllowArbTakes,
  ARB_OFFER_ENABLED,
} from '@/consts/constants';
import { useAPI, generateIdempotencyKey } from '@/composables/useAPI';
import { useUserStore } from '@/stores/user';
import { useExchangeStore } from '@/stores/exchange';
import { usePlaceTrackerStore } from '@/stores/placeTracker';
import {
  betCreatedNotification,
  betCreationFailedNotification,
  noBetCreatedNotification,
  betTakenNotification,
  betTakenAndOrderCreatedNotification,
} from '@/core/notifications';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { emitter } from '@/core/emitter';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faTelegram } from '@fortawesome/free-brands-svg-icons/faTelegram';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { storeToRefs } from 'pinia';
import { ouSideModal } from '@/consts/constants';
import { moneyline1x2SideModal } from '@/consts/constants';
import { useWindowSize } from '@/composables/useWindowSize';
import { validateLineNumber } from '@/core/lineNumber';

const props = defineProps({
  fullGame: {
    type: Object,
    required: true,
  },
  session: {
    type: Object,
    required: true,
  },
  bidLadder: {
    type: Array,
    required: true,
  },
  askLadder: {
    type: Array,
    required: true,
  },
  spreadNumber: {
    type: Number,
    default: 0,
  },
  totalNumber: {
    type: Number,
    default: 0,
  },
  alternativeTotals: {
    type: Array,
    required: false,
    default: () => [],
  },
  alternativeSpreads: {
    type: Object,
    required: false,
    default: () => {},
  },
  // prop for the newOfferModal mode
  isNewOffer: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'returnToParentModal',
  'closeModal',
  'toggleLoading',
  'addWidthToBidLadder',
  'spreadUpdate',
  'sideUpdate',
  'offerPlaced',
]);

const userStore = useUserStore();
const exchangeStore = useExchangeStore();
const placeTracker = usePlaceTrackerStore();
const { oddsComputed } = storeToRefs(exchangeStore);
// Joins offer_placed to the placeUpdateReceived GCP metric for this placement.
const phPlaceAttemptId = ref(null);

const arbOfferEnabled = ref(
  (() => {
    if (userStore.user?.defaultArbOffer) return true;
    try {
      if (localStorage.getItem('defaultArbOffer') === 'true') return true;
    } catch (err) {
      // ignore — fall through to other defaults
    }
    if (
      userStore.user?.viewOddsWithCommission &&
      !alwaysAllowArbTakes.includes(userStore.user?.username) &&
      !userStore.user?.isPro
    ) {
      return true;
    }
    return false;
  })(),
);

// Period token ("1H", "F5", "2H", "Q1" …) for partial-game markets, derived
// from the participant longName. Empty for full-game markets.
const periodLabel = computed(() =>
  getPeriodPrefix(props.fullGame?.participants?.[0]?.longName).trim(),
);

const available = computed(() => {
  const displayBalance = Number(userStore.user.displayBalance || 0);
  const liability = Number(userStore.user.liability || 0);
  const creditLimit = Number(userStore.user.creditLimit || 0);
  return displayBalance + liability - creditLimit;
});

const offerRiskValue = computed(() => Number(offerRisk.value || 0));
const isOverAvailable = computed(() => offerRiskValue.value > available.value);
const isOverMaxLiability = computed(
  () => offerRiskValue.value > Number(userStore.user.maxLiability || 0),
);
const updatingMaxRisk = ref(false);

// PRO-94: Detect when user exceeds balance/maxLiability but can still offer max
const exceedsBalanceButCanOfferMax = computed(() => {
  if (offerRisk.value === '' || parseFloat(offerRisk.value) < 1) return false;
  if (!validateZero.value) return false;
  if (available.value < 1) return false;

  const overBalance = offerRiskValue.value > available.value;
  const overMaxLiability = offerRiskValue.value > Number(userStore.user.maxLiability || Infinity);

  return overBalance || overMaxLiability;
});

// PRO-94: Calculate max risk for display (no commission for offers - maker doesn't pay)
const maxRiskForDisplay = computed(() => {
  const maxLiab = parseFloat(userStore.user.maxLiability) || Infinity;
  return Math.min(Math.floor(available.value), maxLiab);
});

// PRO-94: Calculate win for the max risk amount
const maxWinForDisplay = computed(() => {
  if (maxRiskForDisplay.value <= 0) return 0;
  return calculateWin(maxRiskForDisplay.value, offerOdds.value);
});

// PRO-94: Effective risk for ladder highlighting
const effectiveRiskForLadder = computed(() => {
  const riskNum = parseFloat(offerRisk.value) || 0;

  // When the offer crosses the book and arb-rest mode is OFF, visualize the
  // full crossable range on the user's side as "take" shading even before
  // they've entered a risk amount. This makes it obvious which resting
  // liquidity on your side your offer would consume if placed. Once the user
  // enters a real risk it naturally overrides this preview with the accurate
  // amount.
  if (
    riskNum < 1 &&
    isInArb.value &&
    !arbOfferEnabled.value
  ) {
    const crossableVolume = props.bidLadder.reduce((sum, level) => {
      if (offerOdds.value == null) return sum;
      return offerOdds.value <= level.odds ? sum + (level.volume || 0) : sum;
    }, 0);
    if (crossableVolume > 0) return String(crossableVolume);
  }

  if (riskNum < 1) return offerRisk.value;

  const availNum = Number(available.value) || 0;
  const maxLiabNum = parseFloat(userStore.user.maxLiability) || Infinity;

  if (availNum <= 0) return offerRisk.value;

  const overBalance = riskNum > availNum;
  const overMaxLiability = riskNum > maxLiabNum;

  if (overBalance || overMaxLiability) {
    return maxRiskForDisplay.value.toString();
  }

  return offerRisk.value;
});

async function updateMaxRisk(newMax) {
  if (updatingMaxRisk.value) return;
  updatingMaxRisk.value = true;
  try {
    const { request } = useAPI('/user/updateMaxLiability');
    const response = await request({ maxLiability: newMax });
    if (response.success) {
      userStore.user.maxLiability = newMax;
    }
  } catch (err) {
    console.error('Failed to update max risk:', err);
  } finally {
    updatingMaxRisk.value = false;
  }
}

// consts used when props.isNewOffer
const spreadTotal = ref('');
const selectedSide = ref(null);
const updatedSession = ref({});
const { width: screenWidth } = useWindowSize();

//
/***
 * Functionality for helpful computed values
 */

const betType = ref(props.session.type);
const sideType = computed(() => {
  if (props.session?.type === 'total') {
    return props.session.OU;
  } else if (
    props.session?.type === 'spread' ||
    props.session?.type === 'moneyline'
  ) {
    const participantI = props.fullGame.participants.findIndex(
      (el) => el.id === props.session.participantID,
    );
    const home = props.fullGame.participants[participantI].homeAway === 'home';
    if (props.session?.type === 'spread') {
      return home ? 'homeSpreads' : 'awaySpreads';
    }
    return home ? 'homeMoneylines' : 'awayMoneylines';
  } else if (props.session?.type === 'moneyline1x2') {
    if (props.session.market !== 'draw') {
      const participantI = props.fullGame.participants.findIndex(
        (el) => el.id === props.session.market,
      );
      const home =
        props.fullGame.participants[participantI].homeAway === 'home';
      if (home) {
        return { homeAwayDraw: 'homeMoneylines1x2', side: props.session.side };
      } else {
        return { homeAwayDraw: 'awayMoneylines1x2', side: props.session.side };
      }
    } else {
      return { homeAwayDraw: 'draw1x2', side: props.session.side };
    }
  }
});
const side = computed(() => {
  return {
    type: props.session.type,
    spread: props.session.spread,
    total: props.session.total,
    OU: props.session.OU,
    participantID: props.session.participantID,
  };
});
// ex Over 9.5 or Cleveland Gaurdians
const formattedSideString = computed(() => {
  let number = 0;
  if (betType.value === 'spread') {
    number = spread.value;
  } else if (betType.value === 'total') {
    number = total.value;
  }
  return getSideStringForModals(
    props.fullGame,
    props.session,
    betType.value,
    number,
  );
});

const askLadderComputed = computed(() => {
  const copiedAskLadder = [...props.askLadder];
  // Show the "how others see it" row on the opposing-side ladder whenever the
  // offer will rest on the book. That means: (a) non-crossing orders (normal
  // case), or (b) crossing orders that the user has flipped to an Arb Offer —
  // those rest on the opposite side at the inverse price (e.g. +123 on Celtics
  // rests as -123 on Lakers). Suppress it only for regular crossing orders
  // that will be filled immediately.
  if (!isInArb.value || arbOfferEnabled.value) {
    copiedAskLadder.push(sessionOthersSee.value);
  }
  copiedAskLadder.sort((a, b) => {
    if (a.odds === b.odds) {
      if (a.isUsers && !b.isUsers) return -1;
      if (!a.isUsers && b.isUsers) return 1;
      const aCreatedAt = a.createdAt ? new Date(a.createdAt) : 0;
      const bCreatedAt = b.createdAt ? new Date(b.createdAt) : 0;
      return bCreatedAt - aCreatedAt;
    }
    return b.odds - a.odds;
  });
  return copiedAskLadder;
});

const dontAllowArbs = computed(() => {
  if (
    userStore.user.viewOddsWithCommission &&
    !alwaysAllowArbTakes.includes(userStore.user.username) &&
    !userStore.user.isPro
  ) {
    return true;
  } else {
    return false;
  }
});

// Arb Offer whenever the price crosses the book. For viewOddsWithCommission
// non-pro users it is the only safe path; default on in arbOfferEnabled.
const canOfferAsArb = computed(
  () => ARB_OFFER_ENABLED && isInArb.value,
);
function openArbOfferInfo() {
  emitter.emit('openArbOfferInfoModal');
}

function toggleArbOffer() {
  arbOfferEnabled.value = !arbOfferEnabled.value;
}

//Hooks needed for listener of the dropdowns
onMounted(() => {
  window.addEventListener('click', dropdownListener);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', dropdownListener);
  // Deferred abandoned-slide fire: held until close so a later completed
  // slide in the same open could cancel it (see onSlideConfirm).
  if (phSlideAbandonPending.value) {
    PostHogUtils.capture('bet_confirm_slide', {
      outcome: 'abandoned',
      surface: 'offer',
    });
  }
});

// Slide-to-confirm friction (see TakeBetModal for the full rationale).
// Decision: did replacing the tap with a slider add friction to the final
// step of a mobile offer? The control emits 'abandon' at the attempt
// crossing (not gesture end) so attempts that die without a pointer event
// still count. One abandoned max per modal open; a completed slide cancels
// the pending abandoned. 'completed' measures the gesture, not the API
// result — offer success is offer_placed.
const phSlideAbandonPending = ref(false);
function onSlideAbandon() {
  phSlideAbandonPending.value = true;
}
function onSlideConfirm() {
  phSlideAbandonPending.value = false;
  PostHogUtils.capture('bet_confirm_slide', {
    outcome: 'completed',
    surface: 'offer',
  });
  if (exceedsBalanceButCanOfferMax.value) {
    createOfferAtMax();
  } else {
    createOffer();
  }
}

/***
 * Functionality related to the odds
 */
const offerOdds = ref(100);

function initializeOfferOdds() {
  offerOdds.value = otherSideBestSession.value.odds
    ? -(getAmericanTickInfo(otherSideBestSession.value.odds).next)
    : setOddsWhenNoOtherSide();
  if (offerOdds.value === props.session.odds) offerOdds.value = getAmericanTickInfo(offerOdds.value).next;
  if (offerOdds.value < 100 && offerOdds.value > -100) offerOdds.value = 100;
  // other side best session odds are in american notation
  if (
    (otherSideBestSession.value.odds &&
      userStore.user.oddsFormat === 'decimal' &&
      props.bidLadder.length > 0 &&
      oddsComputed.value <=
        convertAmericanToDecimal(props.bidLadder[0].odds)) ||
    (userStore.user.oddsFormat === 'american' &&
      props.bidLadder.length > 0 &&
      offerOdds.value <= props.bidLadder[0].odds)
  ) {
    offerOdds.value = -otherSideBestSession.value.odds;
  }
  const snapped = getAmericanTickInfo(offerOdds.value).snap;
  const ourBest = props.bidLadder[0]?.odds;
  const theirBest = otherSideBestSession.value?.odds;
  // Market width between two opposite-side taker odds is |ourBest + theirBest|.
  // Flipping one side brings both to the same perspective, so summing gives the
  // signed gap. Using |abs(a) - abs(b)| ignores sign and misidentifies tightness
  // (e.g. would treat -104/-103 as "1 tick" when the real gap is far wider).
  const spread = ourBest && theirBest ? Math.abs(ourBest + theirBest) : null;
  const inHalfTickRange = Math.abs(snapped) < 200;
  // Preserve the half-tick whenever the market is already tight (<= 1 wide).
  // Using `=== 1` dropped through to Math.floor for half-tick spreads, which
  // pulls negative half-ticks the wrong way (-105.5 -> -106) and reverts the
  // suggested offer back to the taker odds the user just clicked.
  if (spread !== null && spread <= 1 && inHalfTickRange) {
    offerOdds.value = snapped;
  } else if (!theirBest) {
    // No liquidity on the opposing side: the only reference price is our own
    // side's best takeable order. Math.floor rounds toward that takeable price
    // (e.g. -110.5 -> -111), which makes the offer cross the book and fill
    // immediately — nonsensical for someone who chose to make an offer rather
    // than take. Round toward better-for-us instead so we post one tick better
    // than we'd take (e.g. -110.5 -> -110).
    offerOdds.value = Math.ceil(snapped);
  } else {
    offerOdds.value = Math.floor(snapped);
  }
  if (offerOdds.value === -100) offerOdds.value = 100;
}

function updateOdds(value) {
  let newOdds = value;
  const odds = parseFloat(newOdds);
  clearLadderSelectionOnManualEdit();
  const prevOdds = offerOdds.value;
  offerOdds.value = odds;
  if (offerOdds.value.toString().replace('-', '').length >= 3) {
    createOfferDisabled.value = true;
  } else {
    createOfferDisabled.value = false;
  }
  handleOddsUpdate(odds, prevOdds);
}

function setOddsWhenNoOtherSide() {
  let bestOddsOurSide;
  let sourcePath = null; // Initialize to null for safety
  if (props.session.type === 'moneyline') {
    bestOddsOurSide = props.fullGame[sideType.value][0]
      ? props.fullGame[sideType.value][0].odds
      : 99;
  } else if (props.session.type === 'moneyline1x2') {
    const side = sideType.value.side === 'yes' ? 'Yes' : 'No';
    if (props.session.market !== 'draw') {
      const key = `${sideType.value.homeAwayDraw?.replace('Moneylines1x2', '')}${side}`;
      bestOddsOurSide = props.fullGame[sideType.value.homeAwayDraw]?.[key]?.[0]
        ? props.fullGame[sideType.value.homeAwayDraw]?.[key]?.[0].odds
        : 99;
    } else {
      const key = `${sideType.value.homeAwayDraw?.replace('1x2', '')}${side}`;
      bestOddsOurSide = props.fullGame[sideType.value.homeAwayDraw]?.[key]?.[0]
        ? props.fullGame[sideType.value.homeAwayDraw]?.[key]?.[0].odds
        : 99;
    }
  } else {
    const number = props.session.type === 'spread' ? spread.value : total.value;
    bestOddsOurSide = props.fullGame[sideType.value][number][0]
      ? props.fullGame[sideType.value][number][0].odds
      : 99;
  }
  return getAmericanTickInfo(bestOddsOurSide).next;
}

const isInArb = computed(() => {
  return (
    (userStore.user.oddsFormat === 'decimal' &&
      props.bidLadder.length > 0 &&
      oddsComputed.value <=
        convertAmericanToDecimal(props.bidLadder[0].odds)) ||
    (userStore.user.oddsFormat === 'impliedProbability' &&
      props.bidLadder.length > 0 &&
      oddsComputed.value >=
        convertAmericanToImpliedProbability(props.bidLadder[0].odds)) ||
    (userStore.user.oddsFormat === 'american' &&
      Math.abs(oddsComputed.value / 100) >= 1 &&
      props.bidLadder.length > 0 &&
      offerOdds.value <= props.bidLadder[0].odds &&
      oddsComputed.value.toString().length >= 3 &&
      offerOdds.value.toString().length >= 3)
  );
});

/***
 * Functionality for changing Risk / Win (Create Offer)
 */
const offerRisk = ref('');
const offerWin = ref('');

// Snapshot the "Default Create Offer Risk" preset as it was when the modal
// opened, so on a successful offer we can tell whether the preset was useful:
// did the user keep it or change the amount before offering? Set by
// applyUnitModePrefill() so the snapshot reflects the displayed risk after the
// unit mode is applied (which can scale risk up on favorites in non-flat modes).
let presetOfferRisk = 0;
let hasOfferPreset = false;

// Prefill risk / win from the user's unit (defaultOffer) per their chosen
// create-offer unit mode, using the finalized offerOdds. Runs once on open
// (after initializeOfferOdds settles the odds); manual edits afterward are
// untouched.
function applyUnitModePrefill() {
  const unit = Number(userStore.user.defaultOffer);
  if (!unit) {
    offerRisk.value = '';
    offerWin.value = '';
    presetOfferRisk = 0;
    hasOfferPreset = false;
    return;
  }
  const odds = offerOdds.value;
  const mode = userStore.user.createOfferUnitMode || 'riskAUnit';
  const winSide = mode === 'winAUnit' || (mode === 'theCardano' && odds <= -100);
  if (winSide) {
    offerWin.value = unit.toFixed(2);
    offerRisk.value = calculateRisk(unit, odds).toFixed(2);
  } else {
    offerRisk.value = unit.toFixed(2);
    offerWin.value = calculateWin(unit, odds).toFixed(2);
  }
  presetOfferRisk = Number(offerRisk.value) || 0;
  hasOfferPreset = presetOfferRisk > 0;
}

function handleOfferRiskUpdate(newRisk) {
  // ✅ Check authentication first
  if (!userStore.user.isLoggedIn) {
    return;
  }
  clearLadderSelectionOnManualEdit();
  if (!newRisk) {
    offerRisk.value = '';
    offerWin.value = '';
    return;
  }
  offerRisk.value = newRisk;
  let floatRisk = parseFloat(newRisk);
  offerWin.value =
    calculateWin(floatRisk, offerOdds.value) === 0
      ? ''
      : calculateWin(floatRisk, offerOdds.value).toFixed(2);
}

function handleOfferWinUpdate(newWin) {
  // ✅ Check authentication first
  if (!userStore.user.isLoggedIn) {
    return;
  }
  clearLadderSelectionOnManualEdit();
  if (!newWin) {
    offerWin.value = '';
    offerRisk.value = '';
    return;
  }
  offerWin.value = newWin;
  const floatWin = parseFloat(newWin);
  offerRisk.value = calculateRisk(floatWin, offerOdds.value).toFixed(2);
}

// Re-anchor risk/win per the user's create-offer unit mode. The anchored
// field's current value holds (a manual edit becomes the new anchor); the
// other field recalculates at the new odds. When The Cardano crosses even
// money the anchored side flips and the amount carries across. Ladder sweep
// clicks bypass the mode — their risk is sized to fill the selected depth.
function handleOddsUpdate(newOdds, prevOdds = newOdds) {
  const mode = userStore.user.createOfferUnitMode || 'riskAUnit';
  const anchor = syncingFromLadder
    ? 'risk'
    : getAnchoredOfferSide(mode, newOdds);
  const carryFrom = syncingFromLadder
    ? 'risk'
    : getAnchoredOfferSide(mode, prevOdds);
  const amount = parseFloat(
    carryFrom === 'win' ? offerWin.value : offerRisk.value,
  );
  if (!amount) {
    offerWin.value = '';
    return;
  }
  if (anchor === 'win') {
    const risk = calculateRisk(amount, newOdds);
    offerWin.value = amount.toFixed(2);
    offerRisk.value = risk === 0 ? '' : risk.toFixed(2);
  } else {
    const win = calculateWin(amount, newOdds);
    offerRisk.value = amount.toFixed(2);
    offerWin.value = win === 0 ? '' : win.toFixed(2);
  }
}

/***
 * Functionality for selecting price levels on your side's ladder (Create Offer)
 *
 * Mirrors the take-bet ladder click: tapping a level selects it and every
 * level above it. The offer's odds are set to the tapped level's price and
 * risk/win fill with the cumulative volume down to that level, so the offer
 * is priced to sweep the selected depth (the arb card and ladder shading then
 * show what would fill immediately).
 */
const selectedBids = ref([]);
const selectedBidIds = computed(() => selectedBids.value.map((bid) => bid.id));

// True only while handleSelectBids is driving odds/risk from a ladder click.
// The odds/risk/win edit handlers below clear the ladder highlight on any
// MANUAL edit, but the programmatic updates a ladder click makes must not wipe
// the very selection they're setting — this flag suppresses that.
let syncingFromLadder = false;

function handleSelectBids(selectBidObjs) {
  if (!userStore.user.isLoggedIn) {
    return;
  }
  const clicked = selectBidObjs[selectBidObjs.length - 1];
  if (!clicked) return;
  syncingFromLadder = true;
  try {
    selectedBids.value = selectBidObjs;
    setOfferOddsFromLevel(clicked.odds);
    // own resting orders can't be matched against, so they contribute no risk
    const filteredBids = selectBidObjs.filter(
      (b) => b.createdBy !== userStore.user.id,
    );
    handleOfferRiskUpdate(getRiskWhenClick(filteredBids).toFixed(2));
  } finally {
    syncingFromLadder = false;
  }
}

// A manual edit to odds/risk/win supersedes a ladder click, so the highlighted
// depth no longer reflects the current offer — drop it (matches take mode,
// where changing the amount re-derives / clears the selection).
function clearLadderSelectionOnManualEdit() {
  if (!syncingFromLadder) {
    selectedBids.value = [];
  }
}

function setOfferOddsFromLevel(levelOdds) {
  const odds = levelOdds === -100 ? 100 : levelOdds;
  // The odds input renders exchangeStore.oddsComputed in the user's odds
  // format, so it must be synced separately when setting odds from code
  // (updateOdds alone only flows OddsSelector -> modal, not back).
  let display = odds;
  if (userStore.user.oddsFormat === 'decimal') {
    display = formatDecimalOddsForInput(convertAmericanToDecimal(odds));
  } else if (userStore.user.oddsFormat === 'impliedProbability') {
    display = convertAmericanToImpliedProbability(odds);
  }
  exchangeStore.setOddsComputed(roundTo3(display));
  updateOdds(odds);
}

function getRiskWhenClick(bids) {
  let valueUntaken = available.value;
  let valueTaken = 0;
  if (Number.parseFloat(valueUntaken) > 0) {
    bids.forEach((bid) => {
      const localValueToTake =
        bid.volume > valueUntaken ? valueUntaken : parseFloat(bid.volume);
      valueTaken += parseFloat(localValueToTake);
      valueUntaken -= parseFloat(localValueToTake);
    });
  }
  return valueTaken;
}

/***
 * Functionality related to offer expiry value (Create Offer)
 */
const expiry = ref('');
const dropdownExpiry = ref(false);
const endExpiry = computed(() => {
  return Math.floor(
    Math.abs(new Date(props.fullGame.start) - new Date()) / 1000 / 60,
  );
});

function initializeExpiry() {
  if (userStore.user.defaultExpiry) {
    expiry.value = userStore.user.defaultExpiry;
  }
  if (props.fullGame.live) {
    expiry.value = '5';
  }
}

function handleExpiryUpdate(newVal) {
  expiry.value = newVal;
}

/***
 * Functionality for the session others see to display in bid ask ladder (Create Offer)
 */
const sessionOthersSee = computed(() => {
  if (!offerOdds.value) return {};
  const pID = otherSideBestSession.value.participantID;
  let americanOdds;
  if (userStore.user.oddsFormat === 'decimal') {
    americanOdds = convertDecimalToAmerican(oddsComputed.value);
  } else if (userStore.user.oddsFormat === 'impliedProbability') {
    americanOdds = convertImpliedProbabilityToAmerican(oddsComputed.value);
  } else {
    americanOdds = oddsComputed.value;
  }
  const formattedOdds = americanOdds === 100 ? 100 : americanOdds * -1;
  const volume =
    offerRisk.value > 0
      ? calculateWinForNoVig(
          offerRisk.value,
          offerOdds.value,
          props.fullGame.league.toLowerCase(),
          props.session.type,
        )
      : 0;
  // Derive the 1x2 opposing-side NAME from the user's actual pick (props.session)
  // via getOtherSideString — the same always-correct derivation the ladder header's
  // other-side-name uses. Do NOT derive it from otherSideBestSession: that is a
  // price-only helper whose populate path varies. When the opposite ladder has
  // resting orders it is seeded from props.askLadder[0] (PR #1714), a resting order
  // whose side/market are not guaranteed on the wire; flipping it via
  // getOtherSideString could land back on the user's own side (PRO-227). Sourcing
  // the name from props.session is correct regardless of the resting-order shape.
  let nameAux;
  if (betType.value === 'moneyline1x2') {
    nameAux = getOtherSideString(props.fullGame, props.session);
  } else {
    nameAux = getSideString(props.fullGame, otherSideBestSession.value);
  }
  return {
    OU: otherSideBestSession.value.OU,
    isUsers: true,
    // Marks the offer being composed, as distinct from the user's EXISTING
    // orders, which the aggregate feed also stamps with isUsers. The ladder's
    // "(Others See)" styling keys on this flag only.
    isProspective: true,
    createdBy: userStore.user.id,
    name: nameAux,
    odds: formattedOdds,
    participantID: pID,
    spread: otherSideBestSession.value.spread,
    vol: isNaN(Number(offerWin.value)) ? 0 : Number(offerWin.value),
  };
});

/***
 *  Functionality for other side best session (Create Offer)
 */
const otherSideBestSession = ref({});

function calcOtherSideBestSession() {
  if (props.session.type === 'moneyline1x2') {
    // For 1x2 markets: keep the same market and side
    // getOtherSideString() handles the YES↔NO flip for display
    const constructedSession = {
      participantID: null,
      odds: null,
      type: props.session.type,
      spread: null,
      market: props.session.market,
      side: props.session.side,
    };
    otherSideBestSession.value = constructedSession;
  } else if (props.session.type !== 'total') {
    const constructedSession = {
      participantID: props.fullGame.participants.find(
        (p) => p.id !== props.session.participantID,
      ).id,
      odds: null,
      type: props.session.type,
      spread:
        props.session.type === 'spread' ? -1 * props.session.spread : null,
      market: props.fullGame.participants.find(
        (p) => p.id !== props.session.participantID,
      ).id,
      side: sideType.value.side,
    };
    otherSideBestSession.value = constructedSession;
    if (
      !otherSideBestSession.value ||
      Array.isArray(otherSideBestSession.value)
    )
      otherSideBestSession.value = constructedSession;
  } else {
    const constructedSession = {
      odds: null,
      type: props.session.type,
      OU: ['over', 'under'].find((x) => x !== props.session.OU),
      total: props.session.total,
    };
    otherSideBestSession.value = constructedSession;
  }
}

function initilizeOtherSideBestSession() {
  const otherSideType = getOtherSideType(sideType.value);
  let sourcePath = null; // Initialize to null for safety

  // Helper function for deep cloning
  const deepClone = (obj) => {
    if (!obj) return obj;
    return structuredClone(toRaw(obj));
  };

  // The board the user sees is the sorted, de-duped opposing ladder
  // (formatBidAndAsk). Seed from its true best so the default offer leads it,
  // rather than the raw unsorted cache read whose [0] can be a worse price.
  if (props.askLadder?.length > 0) {
    sourcePath = props.askLadder[0];
  } else if (
    props.session.type === 'moneyline1x2' &&
    props.session.market !== 'draw'
  ) {
    const key = `${otherSideType.homeAwayDraw?.replace('Moneylines1x2', '')}${otherSideType.side}`;
    sourcePath = props.fullGame[otherSideType.homeAwayDraw]?.[key]?.[0];
  } else if (
    props.session.type === 'moneyline1x2' &&
    props.session.market === 'draw'
  ) {
    const key = `${otherSideType.homeAwayDraw?.replace('1x2', '')}${otherSideType.side}`;
    sourcePath = props.fullGame[otherSideType.homeAwayDraw]?.[key]?.[0];
  } else {
    switch (props.session.type) {
      case 'moneyline':
        if (props.fullGame[otherSideType]?.length > 0) {
          sourcePath = props.fullGame[otherSideType][0];
        }
        break;
      case 'spread':
        if (props.fullGame[otherSideType]?.[-spread.value]?.length > 0) {
          sourcePath = props.fullGame[otherSideType][-spread.value][0];
        }
        break;
      case 'total':
        if (props.fullGame[otherSideType]?.[total.value]?.length > 0) {
          sourcePath = props.fullGame[otherSideType][total.value][0];
        }
        break;
      default:
        sourcePath = null; // Handle unknown types
    }
  }

  if (sourcePath) {
    otherSideBestSession.value = deepClone(sourcePath);
  } else {
    calcOtherSideBestSession();
  }
}

/**
 * Functionality to handle Spreads and totals
 */

const spread = props.spreadNumber ? ref(props.spreadNumber) : ref(0);
const total = props.totalNumber ? ref(props.totalNumber) : ref(0);
const dropdownOfferRisk = ref(false);
const dropdownOfferWin = ref(false);
const bidLadderSpread = ref(false);

// below are not currently used
const awaySpreadPopUp = ref(false);
const totalPopUp = ref(false);

// eventually we can set up the ability to change spread on create offer
function openDropdown() {
  return;
  /*
  bidLadderSpread.value = !bidLadderSpread.value;
  if (props.fullGame.league === 'UFCMMA') {
    return;
  }
  if (props.session.type === 'spread') {
    awaySpreadPopUp.value = true;
  } else {
    totalPopUp.value = true;
  }
    */
}

/***
 * Misc Functionality (Create Offer) (Take Bet)
 */
const loading = ref(false);
// One Idempotency-Key per offer intent, rotated after each completed cycle.
// A duplicated submission replays the first response server-side instead of
// posting a second order.
let offerIntentKey = generateIdempotencyKey();
const isBestOffer = computed(() => {
  if (!offerOdds.value) return '';
  if (-1 * offerOdds.value < otherSideBestSession.value.odds) {
    return 'difference-not-best';
  }
  return 'difference-best';
});

const sendMessage = ref(userStore.user.defaultSendOrderMessage);
function handlePressedUpdate(value) {
  sendMessage.value = value;
}

const isPro = ref(false);

// formerly disabledOrEnabled
const createOfferDisabled = ref(true);

// The line input is free text, and the exchange returns HTTP 200 for an
// invalid line with the rejection buried in the response body — so an unchecked
// entry fails silently. Mirrors go-api place/validation.go; see core/lineNumber.
const lineNumberError = computed(() => {
  if (!props.isNewOffer || props.session?.type?.includes('moneyline')) {
    return null;
  }
  const result = validateLineNumber({
    value: spreadTotal.value,
    type: props.session.type,
    game: props.fullGame,
  });
  return result.ok ? null : result.error;
});

// Mobile shows a slide-to-confirm in place of the tap button (<768px).
const isMobileViewport = computed(() => screenWidth.value < 768);
// Single source of truth mirroring the button's :disabled expression, shared
// by the slider and the desktop fallback so they can never diverge.
const offerConfirmDisabled = computed(
  () =>
    loading.value ||
    !!lineNumberError.value ||
    offerWin.value < 25 ||
    Math.abs(offerOdds.value) < 100 ||
    !createOfferDisabled.value ||
    (userStore.user.oddsFormat === 'decimal' && oddsComputed.value < 1.01) ||
    !validateZero.value ||
    available.value < 1 ||
    updatingMaxRisk.value ||
    (dontAllowArbs.value && isInArb.value && !arbOfferEnabled.value),
);

const onFocusValues = ref({
  risk: false,
  win: false,
  createOfferRisk: false,
  createOfferWin: false,
  odds: false,
  expiry: false,
  keyboard: false,
  max: true,
});

const offerBelowMinimum = computed(
  () =>
    !onFocusValues.value.createOfferWin &&
    !onFocusValues.value.createOfferRisk &&
    Number(offerWin.value) > 0 &&
    Number(offerWin.value) < 25,
);

function secondsByLeague() {
  const { league } = props.fullGame;
  if (league.includes('NFL') || league.includes('NBA')) {
    return 5;
  } else if (league.includes('NCAAF')) {
    return 6;
  } else if (league.includes('NCAAB')) {
    return 5;
  } else if (league.includes('UFCMMA')) {
    return 3;
  }
  return 5;
}

const validateZero = computed(() => {
  const win = offerWin.value;
  const risk = offerRisk.value;
  if (risk[0] === '0' || win[0] === '0') {
    return false;
  }
  return true;
});

const arbLeftUntaken = ref(null);
const arbOrderMessage = computed(() => {
  if (dontAllowArbs.value) {
    return 'Offer is in arbitrage and will not be placed unless Arb Offer is on.';
  }
  if (!arbLeftUntaken.value || arbLeftUntaken.value < 25)
    return 'This order will be filled immediately, please check this offer!';
  return `$${(offerRisk.value - arbLeftUntaken.value).toFixed(2)} will be filled immediately, $${arbLeftUntaken.value.toFixed(2)} will be created as an order`;
});

// don't shade in anything at a worse price than the offer odds
// calculate how much of the offer will be taken vs created as a new offer
function passWidthLadderArray(widthArr) {
  arbLeftUntaken.value = null;
  const adjWidthLadder = [];
  let totalVolume = 0;
  for (let i = 0; i < widthArr.length; i++) {
    if (offerOdds.value > props.bidLadder[i].odds) {
      break;
    } else {
      adjWidthLadder.push(widthArr[i]);
      totalVolume += props.bidLadder[i].volume;
      if (widthArr[i] === '100%') {
        if (
          offerOdds.value > props.bidLadder[i + 1]?.odds ||
          !props.bidLadder[i + 1]
        ) {
          arbLeftUntaken.value = offerRisk.value - totalVolume;
        }
      }
    }
  }
  emit('addWidthToBidLadder', adjWidthLadder);
}

/***
 * Set up / Tear Down Create offer Mode (Create Offer)
 */
function setUpCreateOffer() {
  initializeUpdatedSession();
  initilizeOtherSideBestSession();
  if (props.isNewOffer) {
    initializeSpreadTotal();
    initializeSide();
  }
  initializeOfferOdds();
  initializeExpiry();
  applyUnitModePrefill();
}

function handleReturnToParentModal() {
  emit('returnToParentModal');
}

/***
 * Functionality to submit offer (Create Offer);
 */

// PRO-94: Create offer at max available amount
async function createOfferAtMax() {
  if (loading.value) {
    // Guard fired = a duplicate tap actually reached the handler. Decision:
    // is the double-submit guard doing real work on real users, or dead
    // weight (over-firing would silently eat legit offers)?
    PostHogUtils.capture('duplicate_submit_blocked', { surface: 'offer' });
    return;
  }
  const maxRisk = maxRiskForDisplay.value;
  offerRisk.value = maxRisk.toFixed(2);
  offerWin.value = calculateWin(maxRisk, offerOdds.value).toFixed(2);
  await createOffer();
}

// Fired on a successful offer. Tracks whether the "Default Create Offer Risk"
// preset was useful: among users who have a preset, did they keep it or change
// the amount before offering?
function captureOfferPlaced() {
  const finalRisk = parseFloat(offerRisk.value);
  // changed_preset: only meaningful when a preset exists. Compare at 2 decimals
  // because the field is prefilled with defaultOffer.toFixed(2) — so a preset
  // like 50.005 shows as "50.01" and an untouched offer must count as "kept",
  // not a change.
  const changedPreset = hasOfferPreset
    ? !(
        Number.isFinite(finalRisk) &&
        finalRisk.toFixed(2) === presetOfferRisk.toFixed(2)
      )
    : null;
  // Sign of the offered American odds (not the value) so we can segment
  // behaviour by underdog (+) vs favorite (-) without storing price data.
  // +100 (even money) counts as positive by convention.
  const finalOdds = Number(offerOdds.value);
  const offerOddsSign = Number.isFinite(finalOdds)
    ? finalOdds >= 100
      ? 'positive'
      : 'negative'
    : null;
  PostHogUtils.capture('offer_placed', {
    has_preset: hasOfferPreset,
    // Which unit mode produced the prefill (PRO-173) — the kept-vs-changed
    // split by mode is the feature's success metric (does Win-a-Unit /
    // The Cardano reduce hand-editing on favorites?).
    unit_mode: userStore.user.createOfferUnitMode || 'riskAUnit',
    is_live: !!props.fullGame?.live,
    market_type: props.fullGame?.league?.includes('PROPS')
      ? 'prop'
      : props.session?.type,
    // Exotic-market label — did the June World Cup market build (To Advance,
    // 1x2, euro handicap, 1H lines) produce any offer volume?
    market_subtype: getMarketSubtype(props.fullGame, props.session?.type),
    ...(changedPreset !== null && { changed_preset: changedPreset }),
    ...(offerOddsSign !== null && { offer_odds_sign: offerOddsSign }),
    // Joins to the place tracker's GCP placeUpdateReceived metric for this offer.
    ...(phPlaceAttemptId.value && {
      place_attempt_id: phPlaceAttemptId.value,
    }),
  });
}

async function createOffer() {
  // Synchronous re-entrancy guard — :disabled only applies on the next
  // render, so queued taps would otherwise each submit.
  if (loading.value) {
    // See createOfferAtMax — is the guard firing on real users?
    PostHogUtils.capture('duplicate_submit_blocked', { surface: 'offer' });
    return;
  }
  // Declared outside try so finally reports the terminal status exactly once.
  let placeAttemptId = null;
  let httpStatus = 'rejected';
  // Place response leg for exact order-ID side-update matching (unmatched/matched orderID).
  let placeSessionForTelemetry = null;
  try {
    loading.value = true;
    let sideString;
    let number = 0;

    if (betType.value === 'spread') {
      number = spread.value;
    } else if (betType.value === 'total') {
      number = total.value;
    }

    const baseSideString = getSideStringForModals(
      props.fullGame,
      props.session,
      betType.value,
      number,
    );
    // Rotation numbers are only populated on traditional sportsbook games.
    // Player-prop and 1H child games don't carry them, so a naive prefix
    // renders "undefined Under 3" in the success toast.
    const wantRotation =
      userStore.user.displayRotationNumbers && betType.value !== 'moneyline1x2';
    const rotationParticipant = wantRotation
      ? betType.value === 'total'
        ? props.fullGame.participants?.find((p) => p.homeAway === 'away')
        : props.fullGame.participants?.find(
          (p) => p.id === props.session.participantID,
        )
      : null;
    const rotationNumber = rotationParticipant?.rotationNumber;
    sideString =
      rotationNumber != null
        ? `${rotationNumber} ${baseSideString}`
        : baseSideString;

    const PLACE_ROUTE = 'session/v2/place/';
    const { request } = useAPI(PLACE_ROUTE);

    const order = {
      gameID: props.fullGame.id,
      odds: getAmericanTickInfo(offerOdds.value).snap,
      bet: parseFloat(offerRisk.value),
      type: betType.value,
      number: null,
      side:
        props.session.type === 'total'
          ? props.session.OU
          : props.session.participantID,
      expirationMinutes: Math.floor(parseFloat(expiry.value)),
      orderType: (() => {
        // Arb Offer lives on the book even when it crosses the opposing side.
        // The go-api recognizes this as its own OrderType (see post-arb PR).
        if (
          ARB_OFFER_ENABLED &&
          arbOfferEnabled.value &&
          canOfferAsArb.value
        ) {
          return 'postArb';
        }
        return dontAllowArbs.value && !userStore.user.isPro ? 'post' : 'limit';
      })(),
    };

    if (order.type === 'total') {
      order.number = total.value;
    } else if (order.type === 'spread') {
      order.number = spread.value;
    } else if (props.session.type === 'moneyline1x2') {
      order.side = props.session.side;
      order.market = props.session.market;
    }

    const payload = [order];

    // Open the telemetry attempt right before the request; finally reports the HTTP result.
    placeAttemptId = placeTracker.register({
      orderType: order.orderType,
      gameId: props.fullGame.id,
      marketType: props.fullGame?.league?.includes('PROPS')
        ? 'prop'
        : props.session?.type,
      isLive: !!props.fullGame?.live,
      leagueId: props.fullGame?.league ?? null,
      sportId: props.fullGame?.sport ?? null,
    });
    phPlaceAttemptId.value = placeAttemptId;

    const response = await request(
      {
        orders: payload,
        sendMessage: sendMessage.value,
      },
      null,
      { idempotencyKey: offerIntentKey },
    );
    if (
      !response.createdSessions ||
      !Array.isArray(response.createdSessions) ||
      response.createdSessions.length === 0
    ) {
      noBetCreatedNotification();
      return;
    }
    const createdSessions = response.createdSessions[0];
    if (!createdSessions) return;
    if (
      createdSessions.unmatched &&
      createdSessions.matched &&
      Object.keys(createdSessions.unmatched).length > 0 &&
      createdSessions.matched.length > 0
    ) {
      betTakenAndOrderCreatedNotification({
        sideString: sideString,
        amount: offerRisk.value,
        odds: formatOddsNoCommission(offerOdds.value),
      });
      httpStatus = 'ok';
      placeSessionForTelemetry = createdSessions;
      captureOfferPlaced();
      emit('offerPlaced');
    } else if (
      createdSessions.unmatched &&
      createdSessions.matched &&
      (Object.keys(createdSessions.unmatched).length > 0 ||
        createdSessions.matched.length > 0)
    ) {
      const notifPackage = {
        sideString,
        amount: offerRisk.value,
        odds: formatOddsNoCommission(offerOdds.value),
      };
      betCreatedNotification(notifPackage, currentDevice());
      httpStatus = 'ok';
      placeSessionForTelemetry = createdSessions;
      captureOfferPlaced();
      emit('offerPlaced');
    } else {
      if (createdSessions && createdSessions.error) {
        noBetCreatedNotification(createdSessions.error);
      } else {
        noBetCreatedNotification();
      }
    }

    if (createdSessions.matched && createdSessions.matched.length > 0) {
      emitter.emit('get-me');
    }
  } catch (error) {
    // Latch: once the place itself came back ok, a throw from the notification /
    // analytics code below it is a UI bug, not a failed placement. Relabelling it
    // 'error' would book a real success as a failure in the telemetry.
    if (httpStatus !== 'ok') httpStatus = 'error';
    console.log(error);
    let notifError = error;
    if (error.message) {
      notifError = error.message;
    }
    let data = {
      detail: notifError,
    };
    if (typeof notifError === 'string' && notifError.includes('locked')) {
      data = {
        title: 'Account Paused',
        detail: 'Please contact an admin to unlock your account',
      };
    }
    betCreationFailedNotification(data);
  } finally {
    emit('closeModal', true);
    loading.value = false;
    // Guard: early-validation returns above register() leave placeAttemptId null.
    if (placeAttemptId) {
      placeTracker.recordHttpResponse(
        placeAttemptId,
        httpStatus,
        placeSessionForTelemetry,
      );
    }
    offerIntentKey = generateIdempotencyKey();
  }
}

//Computed value with participants options.
const modalSideOptions = computed(() => {
  switch (props.session.type) {
    case 'total':
      return ouSideModal;
    case 'moneyline':
    case 'spread':
      return props.fullGame.participants;
    case 'moneyline1x2':
      let objectOptions;
      if (props.session.market !== 'draw') {
        const participantI = props.fullGame.participants.findIndex(
          (el) => el.id === props.session.market,
        );
        const longname = props.fullGame.participants[participantI].longName;
        objectOptions = [
          { id: 0, longName: `YES ${longname}` },
          { id: 1, longName: `NO ${longname}` },
        ];
      } else {
        const drawLabel = getDrawLabel(props.fullGame);
        objectOptions = [
          { id: 0, longName: `YES ${drawLabel}` },
          { id: 1, longName: `NO ${drawLabel}` },
        ];
      }
      return objectOptions;
    default:
      return null; // or handle default case appropriately
  }
});

//Initialize spreadTotal value with initial spread.
function initializeSpreadTotal() {
  if (props.session.type === 'total') {
    spreadTotal.value = total.value;
  } else {
    if (spread.value > 0) {
      spreadTotal.value = ''.concat('+', spread.value);
    } else {
      spreadTotal.value = spread.value;
    }
  }
}

//Initialize selectedSide with initial value in session.
function initializeSide() {
  if (props.session.type === 'total') {
    selectedSide.value = ouSideModal.find(
      (el) =>
        el.longName.toLocaleLowerCase() ===
        props.session.OU.toLocaleLowerCase(),
    );
  } else if (
    props.session.type === 'moneyline' ||
    props.session.type === 'spread'
  ) {
    selectedSide.value = props.fullGame.participants.find(
      (el) => el.id === props.session.participantID,
    );
  } else if (props.session.type === 'moneyline1x2') {
    selectedSide.value = modalSideOptions.value.find((element) =>
      element.longName.toLocaleLowerCase().includes(props.session.side),
    );
  }
}

//Send spreadTotal value and updatedSession to NewOfferModal to update bidLadded, askLadder and refresh the otherSee and updates the updatedSession value.
async function sendSpread(value) {
  const numValue = Number(value);
  if (isNaN(numValue)) return;
  if (props.session.type !== 'total') {
    updatedSession.value.spread = numValue;
    spread.value = numValue;
  } else {
    updatedSession.value.total = numValue;
    total.value = numValue;
  }
  await emit('spreadUpdate', {
    updatedSpread: numValue,
    updatedSession: updatedSession.value,
  });
  await calcOtherSideBestSession();
}

//Send sideSelected value and updatedSession to NewOfferModal to update bidLadded, askLadder, refresh the otherSee and updates the updatedSession value.
async function sendSide() {
  if (props.session.type === 'moneyline' || props.session.type === 'spread') {
    updatedSession.value.participantID = selectedSide.value.id;
  } else if (props.session.type === 'total') {
    updatedSession.value.OU = selectedSide.value.longName.toLowerCase();
  } else if (props.session.type === 'moneyline1x2') {
    updatedSession.value.side = selectedSide.value.longName
      .toLowerCase()
      .includes('yes')
      ? 'yes'
      : 'no';
  }
  await emit('sideUpdate', {
    updatedSide: selectedSide.value,
    updatedSession: updatedSession.value,
  });
  await calcOtherSideBestSession();
}

// Initialize a copy of the initial session value.
function initializeUpdatedSession() {
  updatedSession.value = { ...props.session };
  updatedSession.value.spread = spread.value;
  updatedSession.value.total = total.value;
}

//Function to close dropdowns when clicked outside
function dropdownListener(e = {}) {
  if (
    dropdownOfferRisk.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownOfferRisk.value = false;
  }
  if (
    dropdownOfferWin.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownOfferWin.value = false;
  }
  if (
    dropdownExpiry.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownExpiry.value = false;
  }
}

setUpCreateOffer();
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

$blue1: #dfe1ef;
$blue2: #343b81;
$border-radius-button: 35px;

h1 {
  font-size: 1.5rem;
  color: main.$blue-1100;
  font-weight: bold;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 1rem;
  }
}

h4 {
  font-size: 0.85rem;
  font-weight: bold;
}

h5 {
  font-size: 0.8rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.5rem;
  }
}

h6 {
  color: main.$blue-1100;
  font-weight: main.$font-weight-light;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.7rem;
  }
}

.h2 {
  font-size: main.$font-size-sm;
  color: main.$blue-1100;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.8rem;
  }
}

.available {
  color: main.$green-500;
  cursor: pointer;
}

input[type='text'] {
  flex-grow: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: bold;
}

//this difference not best was put like this
.difference-best.header-info {
  color: main.$color-primary_01;
}

.difference-not-best.header-info {
  color: main.$color-primary_01;
}

.error {
  color: main.$red-500;
}

.warning {
  color: main.$yellow-400;
}

.notBestAlert {
  color: main.$color-primary;
}

.fontsm {
  font-size: small;
}

.create-offer-btn {
  width: 100%;
  font-size: 0.925rem;
  padding: 0.875rem 1rem;
  background-color: main.$grey-1100_03;
  color: main.$blue-800_01;
  border-radius: $border-radius-button;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }

  &:focus {
    background: main.$grey-2000;
  }

  &.btn-max-balance {
    background-color: main.$orange-100;
    color: main.$white;

    &:hover {
      filter: brightness(0.95);
    }
  }
}

.createOfferActual {
  width: 100%;
  padding: 0.85rem 3rem;
  font-size: main.$font-size-sm;
}

.fillNext {
  width: 100%;
  font-size: 1rem;
  padding: 0.875rem 1rem;
  background-color: main.$green-500;
  border-radius: $border-radius-button;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }
}

.take-bet-btn {
  width: 100%;
  font-size: 0.925rem;
  padding: 0.875rem 1rem;
  background-color: main.$blue-800_03;
  color: main.$grey-1100_02;
  border-radius: $border-radius-button;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }

  &:focus {
    background: main.$blue-103_01;
  }
}

.parley-btn {
  width: 100%;
  font-size: main.$font-size-sm;
  padding: 0.875rem 2rem;
}

.log-in-link {
  font-size: main.$font-size-sm;
}

.fourcast-link {
  font-size: main.$font-size-sm;
  color: main.$color-primary;
  border-bottom: 1px solid main.$color-primary;

  &:hover {
    color: main.$color-primary;
    border-bottom: 1px solid main.$transparent;
  }
}

.bet-up-btn {
  cursor: pointer;
  color: main.$color-primary;

  &:hover {
    color: main.$green-500;
  }

  font-size: 1.15rem;

  @media screen and (min-width: map.get(main.$grid-breakpoints, md)) {
    line-height: 1rem;
  }
}

.bet-down-btn {
  cursor: pointer;
  color: main.$red-301;

  &:hover {
    color: main.$blue-100;
  }

  font-size: 1.15rem;

  @media screen and (min-width: map.get(main.$grid-breakpoints, md)) {
    line-height: 1rem;
  }
}

.risk-win-items {
  justify-content: space-between;

  > div {
    position: relative;
  }
}

.order-error-message {
  display: none;
  position: absolute;
  top: -40px;
  left: 0;
  background: main.$white_01;
  box-shadow: 1px 1px 5px -2px main.$blue-100;
  color: main.$black-102;
  padding: 8px;
  font-weight: 400;
  font-size: 0.75rem;
  border-radius: 2px;
  border: 1px main.$grey-101 solid;
  text-align: center;
  text-wrap: wrap;
  z-index: 5;
}

.input-group.error-outline {
  outline: 1px solid main.$red-202;
  box-shadow:
    0 2px 10px 0 main.$blue-1200,
    0 0 0 3px rgba(234, 59, 68, 0.12);
  transition:
    outline-color 0.15s ease,
    box-shadow 0.15s ease;
}

.error-outline {
  &:hover ~ .order-error-message {
    display: block;
  }
}

.offer {
  color: main.$green-500;
}

.comparison {
  color: main.$black;
  font-size: 0.75rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.65rem;
  }
}

.tick {
  background-color: main.$grey-500;
  user-select: none;
}

.telegramTick {
  background-color: main.$grey-402_02;
  user-select: none;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
}

.info {
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  background-color: main.$white-200;
}

//.input-icon-clear {
//  position: absolute;
//  margin-top: 12px;
//  margin-left: -18px;
//  font-size: 0.675rem !important;
//  color: #e29898;
//}

//.input-icon-clear:hover {
//  cursor: pointer;
//  color: #f70000;
//}

.input-group {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  outline: 1px solid main.$grey-300;
  box-shadow: 0 2px 10px 0 main.$blue-1200;
  border-radius: 12px;
  padding: 8px 0 8px 5px;
  color: main.$blue-800_01;
  background-color: main.$white_02;

  input {
    width: calc(100% - 100px);
    background-color: main.$transparent_01;
    padding: 0 0.5rem;
    font-size: 18px;

    &:focus {
      border-bottom-color: main.$transparent_01;
    }
  }

  .input-group-button {
    width: 50px;

    .available {
      color: main.$white;
      background-color: main.$blue-400;
      padding: 5px;
      font-size: 0.8rem;
      border-radius: 8px;
      float: right;
    }
  }

  .input-group-div {
    width: 100px;
    display: inline;
    padding: 0 0.3rem;
    border-left: 2px solid main.$grey-900;
    align-items: center;
    font-weight: 900;
    cursor: pointer;
    font-size: 16px;
    text-align: center;
  }

  &.input-group-win-risk {
    input {
      width: calc(100% - 100px - 50px);
    }
  }
}

.dropdown-menu {
  background-color: main.$white;
  overflow: hidden;
  z-index: 1;
  width: 80px;
  position: absolute;
  border: 1px solid main.$black-200;
  border-radius: 5px;
  top: -200px;
  right: -5px;

  &.expiry {
    width: 100px;
    top: -120px;

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      width: 40%;
    }
  }

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    right: 0;
    width: 40%;
  }

  div {
    width: 100%;
    font-size: 16px;
    padding: 0.5rem 1rem;
    clear: both;
    font-weight: 400;
    text-align: inherit;
    white-space: nowrap;
    background-color: main.$transparent_03;
    border-bottom: 1px solid main.$blue-102;
    cursor: pointer;

    &:last-child {
      border-bottom: main.$transparent;
    }

    &:hover {
      background-color: main.$white-101;
      font-weight: bold;
    }
  }
}

.risk-win-items {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    display: contents;

    > div {
      padding: 0 !important;
      margin: 10px 0;
    }
  }
}

.rebate-line {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(46, 125, 50, 0.08);
  border: 1px solid rgba(46, 125, 50, 0.2);
  border-radius: 8px;

  &__label {
    font-size: 0.85rem;
    color: main.$black-203;
    font-weight: 500;
  }

  &__value {
    font-size: 0.95rem;
    color: #2e7d32;
    font-weight: 700;
  }
}

.risk-win-with-commission {
  font-size: 0.875rem;
  padding-top: 0.5rem;
  text-align: center;
  color: main.$blue-800;
}

.with-commission-amount {
  font-size: 1rem;
  font-weight: bold;
}

.teamTotal {
  font-size: main.$font-size-xs;
}

.max-fill {
  background: main.$grey-100;
  height: 38px;
  border-radius: 10px;

  > img {
    width: 14px;
    height: 14px;
  }

  > span {
    color: main.$color-primary;
    font-size: 14px;
  }
}

.add-balance {
  text-decoration: underline;
  color: main.$red-500;
  font-weight: 700;
  cursor: pointer;
}

.add-available-risk {
  text-decoration: underline;
  color: main.$color-primary;
  font-weight: 700;
  cursor: pointer;
}

.alert-text {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 11px;
    font-weight: bold;
  }
}

.spread-modal-input--invalid {
  border-color: main.$red-100 !important;
  color: main.$red-100;
}

.line-error {
  margin: 6px 0 0;
  color: main.$red-100;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.spread-modal-input {
  border-radius: 0px 6px 6px 0px;
  background-color: main.$white_02;
  color: main.$color-primary_01;
  border: 1px solid main.$color-primary_05;
  font-size: 18px;
  height: 44px;
  font-weight: 600;
  border-left: main.$transparent;
  width: 5rem;
  text-align: center;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    width: 25%;
    margin-bottom: 1rem;
  }
}

.side-modal-select-container {
  position: relative;
  align-items: center;
  width: 14rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    width: 25%;
    margin-bottom: 1rem;
  }
}

.side-modal-select-container-moneyline {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    width: 52%;
  }
}

.side-modal-select {
  border-radius: 6px 0px 0px 6px;
  color: main.$color-primary_01;
  border: 1px solid main.$color-primary_05;
  font-size: 18px;
  height: 44px;
  font-weight: 600;
  width: 100%;
  padding: 2px 2.5rem 0 1rem;
  text-align: center;
  text-align-last: center;
  -moz-text-align-last: center;
  background-color: main.$grey-1100;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.side-modal-select:focus {
  outline: none !important;
}

.side-modal-select-chevron {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: main.$color-primary_01;
  pointer-events: none;
  font-size: 14px;
}

.side-modal-select-moneyline {
  border-radius: 6px 6px 6px 6px;
}

.inputs-div {
  justify-self: center;
  place-self: center;
}

.header-info {
  color: main.$black-203;
}

/* Period sticker (1H / F5 / 2H / Q1 …) shown in the matchup line so the bettor
   clearly sees this is a partial-game market. */
.period-pill {
  display: inline-block;
  vertical-align: middle;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: rgba(37, 99, 235, 0.15);
  color: #1d4ed8;
  line-height: 1.4;

  [theme='night'] & {
    background: rgba(96, 165, 250, 0.18);
    color: #93c5fd;
  }
}

.propStyle {
  display: -webkit-inline-box;
}

/* ============================================================
   Arb Offer: single unified card that contains both the
   "this order would cross the book" warning AND the toggle
   controls. A left accent stripe and a shared tinted background
   tie them together; a hairline divider separates the message
   from the controls but keeps them in the same visual frame.
   Stripe + tint switch from warning (yellow) to success (green)
   when the toggle is flipped on.
   ============================================================ */
$arb-warn-accent: #c99017;
$arb-warn-tint: #fff6e0;
$arb-success-accent: #2fa36a;
$arb-success-tint: #e9f7ef;

.arb-card {
  margin-top: 0.5rem;
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background: $arb-warn-tint;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: inset 4px 0 0 $arb-warn-accent;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &.is-on {
    background: $arb-success-tint;
    box-shadow: inset 4px 0 0 $arb-success-accent;
  }

  [theme='night'] & {
    background: rgba(201, 144, 23, 0.16);
    border-color: rgba(255, 255, 255, 0.06);

    &.is-on {
      background: rgba(47, 163, 106, 0.18);
    }
  }
}
.arb-card-message {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.55rem 0.85rem 0.55rem 1rem;
  color: $arb-warn-accent;

  .arb-card.is-on & {
    color: $arb-success-accent;
  }
}
.arb-card-status-icon {
  flex-shrink: 0;
  font-size: 0.9rem;
}
.arb-card-text {
  font-size: 0.9rem;
  line-height: 1.35;
  font-weight: main.$font-weight-semibold;

  @media screen and (max-width: map.get(main.$grid-breakpoints, 'sm')) {
    font-size: 0.8rem;
  }
}
.arb-card-controls {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.85rem 0.55rem 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.55);

  [theme='night'] & {
    background: rgba(255, 255, 255, 0.04);
    border-top-color: rgba(255, 255, 255, 0.06);
  }
}
.arb-card-title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0;
  cursor: pointer;
  user-select: none;
}
.arb-card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(111, 117, 177, 0.22);
  color: main.$color-primary_01;
  font-size: 0.72rem;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease;

  .arb-card.is-on & {
    background: $arb-success-accent;
    color: main.$white_01;
  }
}
.arb-card-label {
  font-weight: main.$font-weight-semibold;
  font-size: 0.95rem;
  color: main.$blue-1100;
}
.arb-card-learn {
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.82rem;
  color: main.$color-primary_01;
  opacity: 0.75;
  white-space: nowrap;

  &:hover {
    opacity: 1;
  }
}
.arb-card-spacer {
  flex: 1 1 auto;
}
.arb-card-state {
  font-size: 0.78rem;
  font-weight: main.$font-weight-semibold;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.55;
  color: main.$blue-1100;

  .arb-card.is-on & {
    color: $arb-success-accent;
    opacity: 1;
  }
}
.arb-card .toggle-slider.is-on {
  background: $arb-success-accent;
}
.arb-card {
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid main.$color-primary_01;
    outline-offset: 2px;
  }
}
@media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
  .arb-card-controls {
    flex-wrap: wrap;
    row-gap: 4px;
  }
  .arb-card-learn {
    order: 4;
    flex-basis: 100%;
    text-align: left;
  }
}

/* Toggle switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  flex-shrink: 0;
  pointer-events: none;
}
.toggle-slider {
  position: absolute;
  inset: 0;
  background: #c4c4c4;
  border-radius: 24px;
  transition: background 0.2s ease;

  &.is-on {
    background: main.$color-primary_01;

    &::before {
      transform: translateX(18px);
    }
  }

  &::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
}

</style>
